/**
 * A sector campaign in the game (BUILDLIST 56.13): the controller between the
 * pure sector model and the screen. Turn mode (WeGo): the player orders Blue,
 * the stand-in commander orders the rest, Resolve turn runs the clock, and the
 * turn pauses at each battle for a decision — auto-resolve in a worker, watch
 * (the rendered match, all bots), or take command (the player on Blue's units).
 * A watched or commanded battle hands its final state back through
 * `battleDone`, and the turn continues.
 *
 * The resolver is injected so tests drive it without a worker; the game gives
 * it the battle worker.
 */
import { SCENARIOS, createScenario } from '../sector/scenario';
import { placeDecoy, standInDecoys } from '../sector/decoys';
import type { SectorRules } from '../sector/types';
import { siteHistoryLines } from '../sector/history';
import type { KitPresetKey } from '../core/unitModifiers';
import type { GameState } from '../types';
import type { BattleOutcome, BattleSetup, Order, SectorState, SquadId } from '../sector/types';
import { createSector, issueOrder, battleLengthText, type SectorOptions, buyDrones, refitSquad, buyAmmo, markSystem, unmarkSystem } from '../sector/sector';
import { planFactionTurn, standInLogistics } from '../sector/sectorAi';
import { beginTurn, continueTurn, continueTurnWithModel, enterBattle, leaveBattleStep, type TurnStep } from '../sector/turn';
import { headlessOptionsFor, outcomeFromState } from '../sector/battle';
import type { MatchSetup } from '../match/setup';
import { sectorFromJson, sectorToJson } from '../sector/save';
import type { BattleWorkerRequest, BattleWorkerResponse } from '../sector/battleWorker';

/** 'realtime' (Phase 59.5): take command on the action clock — over the shoulder, in real time. */
export type BattleMode = 'auto' | 'watch' | 'command' | 'realtime';

export interface SectorSessionHost {
  /** Play this battle in the game (the match setup, planets included): a human team, or none for watch. The game calls `battleDone` at game over. */
  startBattle(setup: MatchSetup, humanTeam: number | null): void;
  /** Something changed: re-render. */
  changed(): void;
}

export type BattleResolverFn = (setup: BattleSetup) => Promise<BattleOutcome>;

export const SECTOR_SAVE_KEY = 'orbituary-sector';
/** A scenario's own save slot. */
export function scenarioSaveKey(id: string): string { return `${SECTOR_SAVE_KEY}-scenario-${id}`; }
export const HUMAN_FACTION = 'blue';

export class SectorSession {
  state: SectorState;
  step: TurnStep | null = null;
  /** A battle is being resolved (worker) or played (game). */
  busy: BattleMode | null = null;
  status = '';
  /** 56.16a: the day the turn in progress began (its scout reports are the turn's news). */
  private turnStartDay = 0;
  readonly human = HUMAN_FACTION;

  constructor(state: SectorState, readonly host: SectorSessionHost, private resolve: BattleResolverFn) {
    this.state = state;
  }

  static fresh(seed: string, host: SectorSessionHost, resolve: BattleResolverFn, options: SectorOptions = sectorOptions()): SectorSession {
    return new SectorSession(createSector(seed, options), host, resolve);
  }

  /** A curated scenario (sector/scenario.ts): its own save slot if one is there and unfinished, else a fresh start. */
  static forScenario(id: string, host: SectorSessionHost, resolve: BattleResolverFn, restart = false): SectorSession {
    const def = SCENARIOS[id];
    if (!def) throw new Error(`no scenario ${id}`);
    if (!restart) {
      const saved = SectorSession.loadSaved(host, resolve, scenarioSaveKey(id));
      if (saved instanceof SectorSession && !saved.state.scenario?.result) return saved;
    }
    return new SectorSession(createScenario(def), host, resolve);
  }

  /** Where this sector saves: a scenario has its own slot, so a random sector's save is never overwritten by it. */
  get saveKey(): string { return this.state.scenario ? scenarioSaveKey(this.state.scenario.id) : SECTOR_SAVE_KEY; }

  /** Load what this sector's slot holds (the footer's LOAD). */
  loadSlot(): boolean {
    let json: string | null = null;
    try { json = localStorage.getItem(this.saveKey); } catch { /* storage unavailable */ }
    if (!json) { this.status = 'Nothing saved in this slot yet.'; this.host.changed(); return false; }
    return this.loadJson(json);
  }

  /** The battle waiting for a decision, if any. */
  get pending(): BattleSetup | null { return this.step?.pending ?? null; }
  get decided(): boolean { return this.state.victor !== null; }

  /** The player's order for one of Blue's squads (refusals become the status line). */
  order(squadId: SquadId, order: Order): boolean {
    if (this.step || this.state.pending) { this.status = 'Finish the turn first.'; this.host.changed(); return false; }
    const squad = this.state.squads.find(q => q.id === squadId);
    if (!squad || squad.faction !== this.human) return false;
    try { this.state = issueOrder(this.state, squadId, order); this.status = ''; }
    catch (err) { this.status = err instanceof Error ? err.message : String(err); }
    this.host.changed();
    return this.status === '';
  }

  /** 62.3: buy drones for one of the player's squads resting at home or a depot (refusals become the status line). */
  buyDrones(squadId: SquadId, count = 1): boolean {
    if (this.step || this.state.pending) { this.status = 'Finish the turn first.'; this.host.changed(); return false; }
    const squad = this.state.squads.find(q => q.id === squadId);
    if (!squad || squad.faction !== this.human) return false;
    try { this.state = buyDrones(this.state, squadId, count); this.status = ''; }
    catch (err) { this.status = err instanceof Error ? err.message : String(err); }
    this.host.changed();
    return this.status === '';
  }

  /** X2 at L2: mark a system for the player's faction — the one to hit (or to shell); it crosses down into the next battle there. */
  mark(systemId: string, fire = false): boolean {
    if (this.step || this.state.pending) { this.status = 'Finish the turn first.'; this.host.changed(); return false; }
    const already = (this.state.marks ?? []).some(m => m.faction === this.human && m.systemId === systemId && m.fire === fire);
    this.state = already ? unmarkSystem(this.state, this.human, systemId) : markSystem(this.state, this.human, systemId, fire);
    this.status = '';
    this.host.changed();
    return true;
  }

  /** X3: one of the player's squads leaves a decoy where it stands (refusals become the status line). */
  decoy(squadId: string): boolean {
    if (this.step || this.state.pending) { this.status = 'Finish the turn first.'; this.host.changed(); return false; }
    const q = this.state.squads.find(x => x.id === squadId);
    if (!q || q.faction !== this.human) return false;
    try { this.state = placeDecoy(this.state, squadId); this.status = ''; }
    catch (err) { this.status = err instanceof Error ? err.message : String(err); }
    this.host.changed();
    return this.status === '';
  }

  /** X20: take on rounds for one of the player's squads at a rest point (refusals become the status line). */
  buyAmmo(squadId: SquadId, rounds = 4): boolean {
    if (this.step || this.state.pending) { this.status = 'Finish the turn first.'; this.host.changed(); return false; }
    const squad = this.state.squads.find(q => q.id === squadId);
    if (!squad || squad.faction !== this.human) return false;
    try { this.state = buyAmmo(this.state, squadId, rounds); this.status = ''; }
    catch (err) { this.status = err instanceof Error ? err.message : String(err); }
    this.host.changed();
    return this.status === '';
  }

  /** 56.11: refit one of the player's squads to a kit preset at a rest point (refusals become the status line). */
  refit(squadId: SquadId, kit: KitPresetKey): boolean {
    if (this.step || this.state.pending) { this.status = 'Finish the turn first.'; this.host.changed(); return false; }
    const squad = this.state.squads.find(q => q.id === squadId);
    if (!squad || squad.faction !== this.human) return false;
    try { this.state = refitSquad(this.state, squadId, kit); this.status = ''; }
    catch (err) { this.status = err instanceof Error ? err.message : String(err); }
    this.host.changed();
    return this.status === '';
  }

  /** Orders in (the stand-in commands every other faction); the clock runs to the first battle or the turn's end. */
  /**
   * Enter a battle under way: its forecast is set aside and it waits on the battle card for a seat
   * (watch, take command, real time). Between turns only.
   */
  enterRunning(battleId: string): boolean {
    if (this.step || this.state.pending) { this.status = 'Finish the turn first.'; this.host.changed(); return false; }
    try {
      this.step = enterBattle(this.state, battleId);
      this.state = this.step.state;
      this.status = `The battle at ${this.systemName(this.step.pending!.systemId)} is yours to fight.`;
    } catch (err) { this.status = err instanceof Error ? err.message : String(err); this.host.changed(); return false; }
    this.host.changed();
    return true;
  }

  resolveTurn(): void {
    if (this.step || this.state.pending || this.state.victor) return;
    let orders = {};
    for (const f of this.state.factions) if (f.id !== this.human) this.state = standInLogistics(this.state, f.id);   // X20: top up rounds at rest
    for (const f of this.state.factions) if (f.id !== this.human) orders = { ...orders, ...planFactionTurn(this.state, f.id, this.state.scenario?.enemy === f.id ? this.state.scenario.enemyStyle : undefined) };
    for (const f of this.state.factions) if (f.id !== this.human) this.state = standInDecoys(this.state, f.id, orders);   // X3
    this.turnStartDay = this.state.day;
    this.step = beginTurn(this.state, orders, 1);
    this.state = this.step.state;
    this.afterStep();
  }

  /** Decide the pending battle. Auto resolves here; watch and command hand it to the game. */
  async decide(mode: BattleMode): Promise<void> {
    const setup = this.pending;
    if (!setup || this.busy) return;
    this.busy = mode;
    if (mode === 'auto' && this.state.rules.battleModel) {
      // 62.17 step 4: the battle unfolds in the battle model; it lands when it ends, and can be entered meanwhile
      this.busy = null;
      this.status = `The battle at ${this.systemName(setup.systemId)} is under way.`;
      this.step = continueTurnWithModel(this.step!);
      this.state = this.step.state;
      this.afterStep();
      return;
    }
    if (mode === 'auto') {
      this.status = `Resolving the ${setup.opening.infiltration ? 'infiltration' : 'battle'} at ${this.systemName(setup.systemId)}…`;
      this.host.changed();
      try {
        const outcome = await this.resolve(setup);
        this.busy = null;
        this.apply(outcome);
      } catch (err) {
        this.busy = null;
        this.status = `Battle failed: ${err instanceof Error ? err.message : String(err)}`;
        this.host.changed();
      }
      return;
    }
    const commands = mode === 'command' || mode === 'realtime';
    const humanTeam = commands ? setup.sides.findIndex(s => s.faction === this.human) : -1;
    this.status = `${mode === 'realtime' ? 'Taking command in real time' : commands ? 'Taking command' : 'Watching'} at ${this.systemName(setup.systemId)}…`;
    this.host.changed();
    const options = headlessOptionsFor(setup);
    this.host.startBattle(mode === 'realtime' ? { ...options, clock: 'action' } : options, humanTeam >= 0 ? humanTeam : null);
  }

  /** The player left the battle part-way (111): it goes on in the model from where the match stood. */
  leaveBattle(matchState: GameState, seconds: number): void {
    if (!this.pending) return;
    this.busy = null;
    const name = this.systemName(this.pending.systemId);
    this.step = leaveBattleStep(this.step!, matchState, seconds);
    this.state = this.step.state;
    this.status = `You leave the battle at ${name}; it goes on.`;
    this.afterStep();
  }

  /** The game finished the battle it was handed: its final state becomes the outcome (`seconds`: an action battle's length, R7). */
  battleDone(finalState: GameState, seconds?: number): void {
    const setup = this.pending;
    if (!setup) return;
    this.busy = null;
    this.apply(outcomeFromState(setup, finalState, { seconds }));
  }

  private apply(outcome: BattleOutcome): void {
    if (!this.step?.pending) return;
    const name = this.systemName(this.step.pending.systemId);
    this.step = continueTurn(this.step, outcome);
    this.state = this.step.state;
    const win = outcome.winner ?? (this.state.battles[this.state.battles.length - 1]?.outcome.winner ?? null);
    this.status = `${name}: ${win ? `${win} wins` : 'no decision'} in ${battleLengthText(outcome)}${outcome.wentLoud ? '' : ', quiet'}.`;
    this.afterStep();
  }

  private afterStep(): void {
    if (this.step?.done) {
      const n = this.step.battles.length;
      // 56.16a: a scout's report is the turn's news when there is one — what it learned, on screen
      const reports = this.state.log.filter(e => e.kind === 'scout' && e.faction === this.human && e.day > this.turnStartDay - 1e-9).map(e => e.text);
      if (!this.status) this.status = reports.length ? reports.join(' · ') : `Turn ${this.state.turn}: ${n} battle${n === 1 ? '' : 's'}.`;
      this.step = null;
      // a scenario says how it ended (a day-limit loss is not "red holds every home"); a random sector as before
      const sc = this.state.scenario;
      if (this.state.victor) this.status = sc?.result
        ? `${sc.title}: ${sc.result === 'won' ? `won on day ${this.state.day.toFixed(1)}` : this.state.day >= sc.dayLimit - 1e-9 ? 'lost — the day limit came first' : 'lost — your home fell'}.`
        : `${this.state.victor} holds every home — the sector is decided on day ${this.state.day.toFixed(1)}.`;
    }
    this.host.changed();
  }

  systemName(id: string): string { return this.state.systems.find(s => s.id === id)?.name ?? id; }

  /** X16: the record of the place the current battle is fought at, as the player knows it (for the topo map's corner). */
  historyNotes(n = 3): string[] | undefined {
    const systemId = this.pending?.systemId ?? this.step?.pending?.systemId;
    if (!systemId) return undefined;
    const lines = siteHistoryLines(this.state, systemId, this.human, n);
    return lines.length ? lines : undefined;
  }

  // ---- persistence
  toJson(): string { return sectorToJson(this.state); }
  save(): boolean {
    if (this.step) { this.status = 'Finish the turn before saving.'; this.host.changed(); return false; }
    try { localStorage.setItem(this.saveKey, this.toJson()); this.status = 'Saved.'; } catch { this.status = 'Could not save (storage unavailable).'; }
    this.host.changed();
    return true;
  }
  loadJson(json: string): boolean {
    if (this.step) { this.status = 'Finish the turn before loading.'; this.host.changed(); return false; }
    try {
      const { state, staleBattleVersion, unverifiedTerrain, migratedFrom, rebuiltFromEdits } = sectorFromJson(json);
      this.state = state;
      const notes = [
        staleBattleVersion ? 'saved under another battle version: its history stands, new battles may resolve differently' : '',
        migratedFrom !== undefined ? `migrated from save format ${migratedFrom}` : '',
        rebuiltFromEdits?.length ? `${rebuiltFromEdits.length} system${rebuiltFromEdits.length === 1 ? "'s" : "s'"} scars re-applied from the battle record (the terrain changed)` : '',
        unverifiedTerrain ? 'an older save: its scars were placed without a terrain check' : '',
      ].filter(Boolean);
      this.status = notes.length ? `Loaded — ${notes.join('; ')}.` : 'Loaded.';
      this.host.changed();
      return true;
    } catch (err) { this.status = `Could not load: ${err instanceof Error ? err.message : String(err)}`; this.host.changed(); return false; }
  }
  /**
   * The sector saved in this browser, if any. A save that cannot be loaded (R6: terrain from
   * another build) is not dropped silently: the caller gets its reason, so it can say so before
   * a new sector's first save replaces it.
   */
  static loadSaved(host: SectorSessionHost, resolve: BattleResolverFn, key = SECTOR_SAVE_KEY): SectorSession | { error: string } | null {
    let json: string | null;
    try { json = localStorage.getItem(key); } catch { return null; }
    if (!json) return null;
    try {
      const { state } = sectorFromJson(json);
      return new SectorSession(state, host, resolve);
    } catch (err) { return { error: err instanceof Error ? err.message : String(err) }; }
  }
}

/** A new sector's options: the screen's economy, plus the switchboard's rules (`rules`, from the sector screen's RULES panel). */
export function sectorOptions(rules: Partial<SectorRules> = {}): SectorOptions {
  return { rules: { reinforceDays: 4, incomePerSystemPerDay: 6, healPerDay: 15, ...rules }, startingSupply: 36 };
}

// -----------------------------------------------------------------------------
// The battle worker as a resolver
// -----------------------------------------------------------------------------

let worker: Worker | null = null;
let nextId = 0;
const pending = new Map<number, { resolve: (o: BattleOutcome) => void; reject: (e: Error) => void }>();

/** Auto-resolve in the battle worker (one per page; battles queue). */
export function resolveBattleInWorker(setup: BattleSetup): Promise<BattleOutcome> {
  if (!worker) {
    worker = new Worker(new URL('../sector/battleWorker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<BattleWorkerResponse>) => {
      const p = pending.get(e.data.id);
      if (!p) return;
      pending.delete(e.data.id);
      if (e.data.outcome) p.resolve(e.data.outcome); else p.reject(new Error(e.data.error ?? 'battle worker failed'));
    };
    worker.onerror = () => { for (const p of pending.values()) p.reject(new Error('battle worker crashed')); pending.clear(); worker = null; };
  }
  const id = ++nextId;
  const req: BattleWorkerRequest = { id, setup };
  return new Promise((resolve, reject) => { pending.set(id, { resolve, reject }); worker!.postMessage(req); });
}
